import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  clampCompactWidgetOffset,
  clampCompactWidgetTop,
} from "./compact-widget-geometry";
const SWITCHER_Y_KEY = "denker.desktop.compactSwitcherY";
const SWITCHER_POSITION_KEY = "denker.desktop.compactSwitcherX";
const SWITCHER_EDGE_PADDING = 12;
const DRAG_THRESHOLD_PX = 4;

function readStoredPosition(key: string, fallback: number): number {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw === null ? NaN : Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function storePosition(key: string, value: number) {
  try {
    window.localStorage.setItem(key, String(Math.round(value)));
  } catch {
    // Non-fatal: the switcher can still be moved for the current session.
  }
}

export function useCompactWidgetDrag(shellWidth: number) {
  const dragRef = useRef<{
    pointerId: number;
    element: HTMLDivElement;
    startClientX: number;
    startClientY: number;
    startOffsetX: number;
    startTop: number;
    didDrag: boolean;
  } | null>(null);
  const suppressNextClickRef = useRef(false);
  const [offsetX, setOffsetX] = useState(() =>
    readStoredPosition(SWITCHER_POSITION_KEY, 0),
  );
  const [top, setTop] = useState(() => readStoredPosition(SWITCHER_Y_KEY, 8));
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [isDragging, setIsDragging] = useState(false);

  const clampOffset = useCallback(
    (nextOffset: number) => {
      if (typeof window === "undefined") return nextOffset;
      return clampCompactWidgetOffset({
        requestedOffset: nextOffset,
        viewportWidth: window.innerWidth,
        shellWidth,
        edgePadding: SWITCHER_EDGE_PADDING,
      });
    },
    [shellWidth],
  );

  useEffect(() => {
    setOffsetX((current) => clampOffset(current));
    setTop((current) => clampCompactWidgetTop(current, window.innerHeight));

    function handleResize() {
      setViewportHeight(window.innerHeight);
      setTop((current) => clampCompactWidgetTop(current, window.innerHeight));
      setOffsetX((current) => {
        const clamped = clampOffset(current);
        if (clamped !== current) storePosition(SWITCHER_POSITION_KEY, clamped);
        return clamped;
      });
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampOffset]);

  useEffect(() => {
    function handlePointerMove(event: PointerEvent) {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;

      const deltaX = event.clientX - drag.startClientX;
      const deltaY = event.clientY - drag.startClientY;
      if (!drag.didDrag) {
        if (Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX) return;
        try {
          drag.element.setPointerCapture(event.pointerId);
        } catch {
          // Window listeners already track the drag; capture only improves release.
        }
      }

      drag.didDrag = true;
      setIsDragging(true);
      setOffsetX(clampOffset(drag.startOffsetX + deltaX));
      setTop(clampCompactWidgetTop(drag.startTop + deltaY, window.innerHeight));
      event.preventDefault();
    }

    function handlePointerUp(event: PointerEvent) {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;

      if (drag.element.hasPointerCapture?.(drag.pointerId)) {
        drag.element.releasePointerCapture(drag.pointerId);
      }
      dragRef.current = null;
      setIsDragging(false);
      if (drag.didDrag) {
        setTop((current) => {
          storePosition(SWITCHER_Y_KEY, current);
          return current;
        });
        suppressNextClickRef.current = true;
        window.setTimeout(() => {
          suppressNextClickRef.current = false;
        }, 0);
        setOffsetX((current) => {
          const clamped = clampOffset(current);
          storePosition(SWITCHER_POSITION_KEY, clamped);
          return clamped;
        });
      }
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [clampOffset]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      if (
        event.target instanceof Element &&
        event.target.closest(
          "button,input,textarea,select,a,[role='button']",
        ) &&
        !event.target.closest("[data-compact-drag-handle]")
      ) {
        return;
      }
      dragRef.current = {
        pointerId: event.pointerId,
        element: event.currentTarget,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startOffsetX: offsetX,
        startTop: top,
        didDrag: false,
      };
    },
    [offsetX, top],
  );

  const onClickCapture = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!suppressNextClickRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      suppressNextClickRef.current = false;
    },
    [],
  );

  return {
    offsetX,
    top,
    viewportHeight,
    isDragging,
    dragHandlers: {
      onPointerDownCapture: onPointerDown,
      onClickCapture,
    },
  };
}
